/**
 * Fallback rendered when a layout references a primitive that isn't in
 * PRIMITIVE_REGISTRY. Keeps the page from crashing during incremental
 * rollouts (e.g. a new shell ships that names a primitive not yet built,
 * or a forked event references a primitive we've since renamed).
 *
 * Visibly muted but not invisible — surfaces that something is missing
 * to anyone looking at the page in development. Producers in production
 * should not see this; it indicates a registry / layout drift.
 */

import { Notice } from '@/components/play'

interface Props {
  primitiveName: string
  instanceId: string
}

export function UnknownPrimitive({ primitiveName, instanceId }: Props) {
  if (import.meta.env.PROD) {
    // Silently render nothing in production — don't shout drift at viewers.
    return null
  }

  return (
    <Notice tone="warning" title="Unknown primitive" data-instance-id={instanceId}>
      <p className="font-mono">{primitiveName}</p>
      <p>
        This layout references a primitive that isn't registered. Add it to
        PRIMITIVE_REGISTRY or remove the instance from the layout.
      </p>
    </Notice>
  )
}
